<template>
  <Teleport to="body">
    <dialog ref="dialog" class="view-dialog public-views-info-dialog" :class="{'is-museum':museum,'gallery-dialog-veil':museum,'is-note':note,'is-centred':centred,'is-dark':dark,'is-closing':closing}" :style="viewportStyle" :aria-labelledby="note ? undefined : 'public-views-info-title'" :aria-label="note ? 'Henry’s note' : undefined" aria-describedby="public-views-info-copy" @cancel.prevent="leave('close')" @keydown.stop>
      <header class="view-dialog-head">
        <h2 v-if="!note" id="public-views-info-title"><GalleryMark v-if="museum"/>{{ museum ? 'The View Gallery' : 'About Views' }}</h2>
        <GalleryDesignLink v-if="museum && !note" class="public-views-design-entry" @design="openDesign"/>
        <button ref="close" type="button" class="view-dialog-close" aria-label="Close dialog" @click="leave('close')">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
        </button>
      </header>
      <ViewAboutContent v-if="note" id="public-views-info-copy" :article="!museum" @design="openDesign" @project="leave('close')"/>
      <div v-else-if="museum" id="public-views-info-copy" class="public-views-info-copy gallery-introduction">
        <p>A <AiViewTerm /> connects Henry’s work, ideas and experiences around a visitor’s question. The Gallery brings these readings together as exhibitions you can step inside and explore.</p>
        <p><strong>Browse</strong> previews the exhibitions. Choose one, then enter it through the <strong>Entrance</strong>. <strong>Create</strong> lets AI build your own exhibition around your perspective on Henry. <strong>Archive</strong> lets you browse Henry’s original material.</p>
      </div>
      <div v-else id="public-views-info-copy" class="public-views-info-copy">
        <p>Each <AiViewTerm /> begins with a visitor&rsquo;s question or perspective. AI brings together Henry&rsquo;s work, ideas and experiences, then composes a narrative around the connections it finds.</p>
        <p>The Gallery gives that reading a physical form. Its rooms place the narrative beside the original sources that support it. The same <AiViewTerm /> is also available as an article.</p>
        <p>The collection gathers <AiViewTerm plural /> that visitors have chosen to make public. Visitors can follow their questions through the Gallery and leave a note after the exhibition.</p>
      </div>
      <div v-if="museum && !note" class="gallery-info-media">
        <div class="gallery-info-media-tabs" role="tablist" aria-label="Explore the Gallery">
          <button v-for="(item,index) in mediaTabs" :id="`gallery-info-tab-${item.id}`" :key="item.id" type="button" role="tab" :aria-selected="media===item.id" :tabindex="media===item.id?0:-1" :aria-controls="`gallery-info-panel-${item.id}`" @click="selectMedia(item.id)" @keydown="mediaKey($event,index)">{{ item.label }}</button>
        </div>
        <div class="gallery-info-media-panels">
        <div id="gallery-info-panel-film" class="gallery-info-media-panel" :class="{'is-selected':media==='film'}" :inert="media!=='film'" :aria-hidden="media!=='film'" role="tabpanel" aria-labelledby="gallery-info-tab-film">
          <GalleryFilm :dark="dark" :active="media==='film' && !closing" />
        </div>
        <div id="gallery-info-panel-plan" class="gallery-info-media-panel" :class="{'is-selected':media==='plan'}" :inert="media!=='plan'" :aria-hidden="media!=='plan'" role="tabpanel" aria-labelledby="gallery-info-tab-plan">
          <MuseumFloorPlan :area="area" :dark="dark" @navigate="leave('navigate',$event)"/>
        </div>
        </div>
      </div>
      <button v-if="museum && !note && tourAvailable" class="public-views-tour" type="button" @click="leave('tour')"><span>Take the Gallery tour</span><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8"/><path d="m8 6.5 5 3.5-5 3.5Z"/></svg></button>
      <p v-if="!note && !museum" class="public-views-info-removal">To remove a <AiViewTerm /> you&rsquo;ve shared, <a href="mailto:whenry6688@gmail.com?subject=Public%20View%20removal">email Henry</a> with its link.</p>
    </dialog>
    <MuseumDesignDialog v-if="designOpen" :dark="designDark" :inside-gallery="museum" @close="designOpen=false"/>
  </Teleport>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import GalleryMark from './GalleryMark.vue';
import AiViewTerm from '../ui/AiViewTerm.vue';
import ViewAboutContent from './ViewAboutContent.vue';
import MuseumFloorPlan from './MuseumFloorPlan.vue';
import GalleryFilm from '../ui/GalleryFilm.vue';
import GalleryDesignLink from './GalleryDesignLink.vue';
import { initialExhibitionDarkMode } from '../../utils/exhibitionTheme';
import { initDialogBackdrop } from '../../utils/dialogBackdrop';
import { animateGalleryDialog } from '../../utils/galleryDialogMotion';

const mediaPreferenceKey = 'gallery-info-media';
let rememberedMedia = 'film';
function initialMedia() {
  try {
    const saved = localStorage.getItem(mediaPreferenceKey);
    if (saved === 'film' || saved === 'plan') rememberedMedia = saved;
  } catch { /* Retain the choice for this visit when storage is unavailable. */ }
  return rememberedMedia;
}

export default {
  name: 'PublicViewsInfoDialog',
  components: { GalleryMark, AiViewTerm, ViewAboutContent, MuseumFloorPlan, GalleryFilm, GalleryDesignLink, MuseumDesignDialog: defineAsyncComponent(() => import('./MuseumDesignDialog.vue')) },
  props: { note: Boolean, museum: Boolean, centred: Boolean, dark: Boolean, area: {type:String,default:'hall'}, tourAvailable: { type: Boolean, default: true } },
  emits: ['close','tour','navigate','design'],
  data:()=>({viewportStyle:{},closing:false,designOpen:false,designDark:false,media:initialMedia(),mediaTabs:[{id:'film',label:'Introduction'},{id:'plan',label:'Floor plan'}]}),
  methods:{
    selectMedia(id){
      this.media=id;rememberedMedia=id;
      try{localStorage.setItem(mediaPreferenceKey,id);}catch{ /* The in-memory choice remains available. */ }
    },
    mediaKey(event,index){
      const count=this.mediaTabs.length;
      const keys={ArrowRight:(index+1)%count,ArrowLeft:(index+count-1)%count,Home:0,End:count-1};
      if(!(event.key in keys))return;
      event.preventDefault();
      const id=this.mediaTabs[keys[event.key]].id;
      this.selectMedia(id);
      this.$nextTick(()=>this.$refs.dialog.querySelector(`#gallery-info-tab-${id}`).focus());
    },
    openDesign(){if(this.museum)this.leave('design');else{this.designDark=initialExhibitionDarkMode();this.designOpen=true;}},
    async leave(event='close',payload){
      if(this.closing)return;
      this.closing=true;
      if(this.museum){
        this._exit=animateGalleryDialog(this.$refs.dialog,false,this._entrance);
        await this._exit?.finished.catch(()=>{});
      }
      if(!this._destroyed)this.$emit(event,payload);
    },
    syncViewport(){const v=window.visualViewport,w=v?.width||window.innerWidth,h=v?.height||window.innerHeight;this.viewportStyle={'--dialog-centre-x':`${(v?.offsetLeft||0)+w/2}px`,'--dialog-centre-y':`${(v?.offsetTop||0)+h/2}px`,'--dialog-height':`${h}px`,'--dialog-width':`${w}px`};}},
  mounted() {
    this.syncViewport();
    this._viewport=window.visualViewport;
    this._viewport?.addEventListener('resize',this.syncViewport);this._viewport?.addEventListener('scroll',this.syncViewport);window.addEventListener('resize',this.syncViewport);
    this._trigger = document.activeElement;
    this._releaseScroll = lockBodyScroll();
    this.$refs.dialog.showModal();
    if (this.museum) this._entrance = animateGalleryDialog(this.$refs.dialog,true);
    this._stopBackdrop = initDialogBackdrop(this.$refs.dialog, () => this.leave('close'));
    this.$refs.close.focus({ preventScroll: true });
  },
  beforeUnmount() {
    this._destroyed=true;this._exit?.cancel();
    this._viewport?.removeEventListener('resize',this.syncViewport);this._viewport?.removeEventListener('scroll',this.syncViewport);window.removeEventListener('resize',this.syncViewport);
    this._entrance?.cancel();
    this._stopBackdrop?.();
    this.$refs.dialog?.close();
    this._releaseScroll?.();
    if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
  },
};
</script>

<style scoped src="../../assets/css/viewDialog.css"></style>
<style scoped src="../../assets/css/galleryDialogVeil.css"></style>

<style scoped>
#public-views-info-title{display:flex;align-items:center;gap:12px;}
#public-views-info-title .gallery-mark{width:30px;height:28px;}
.public-views-info-dialog { position:fixed;inset:auto;margin:0;top:var(--dialog-centre-y,50dvh);left:var(--dialog-centre-x,50vw);transform:translate(-50%,-50%);width:min(540px,calc(var(--dialog-width,100vw) - 32px));max-height:calc(var(--dialog-height,100dvh) - 32px);overflow-y:auto;scrollbar-gutter:auto; }
.public-views-info-dialog .view-dialog-head { margin-bottom: 20px; }
.public-views-info-dialog.is-note{padding-top:48px}
.public-views-info-dialog.is-note .view-dialog-head{position:absolute;top:16px;right:20px;margin:0}
.public-views-info-copy p { margin: 0; line-height: 1.65; }
.public-views-info-copy p + p { margin-top: 14px; }
.public-views-info-action { color: var(--site-text-primary); font-weight: 500; }
.public-views-info-dialog .public-views-info-removal { margin: 22px 0 0; padding-top: 18px; border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent); font-size: 12px; line-height: 1.65; }
.public-views-info-removal a { color: var(--site-text-primary); text-decoration-color: color-mix(in srgb, currentColor 30%, transparent); text-underline-offset: 3px; cursor: var(--site-interactive-cursor); transition: color 140ms ease, text-decoration-color 140ms ease; }
.public-views-info-dialog.is-museum{--site-bg:var(--ex-paper,#f5f3ee);--site-text-primary:var(--ex-ink,#36332f);--site-text-secondary:var(--ex-muted,#78716a);width:min(820px,calc(var(--dialog-width,100vw) - 40px));max-height:calc(var(--dialog-height,100dvh) - 40px);padding:30px 34px 24px;border-radius:4px;border-color:var(--ex-border,#d7d1c8);box-shadow:0 18px 60px #17141026;font:14px/1.65 'Quattrocento Sans',sans-serif}
.gallery-introduction{max-width:680px}
.public-views-design-entry{flex:none;margin-left:auto}
.public-views-info-dialog.is-museum :deep(.museum-floor-guide){margin-top:0;padding-top:0;border-top:0}
.gallery-info-media{margin-top:20px}
.gallery-info-media-panels{display:grid;align-items:center}
.gallery-info-media-panel{grid-area:1/1;min-width:0;visibility:hidden;opacity:0;pointer-events:none}
.gallery-info-media-panel.is-selected{visibility:visible;opacity:1;pointer-events:auto}
@media(max-width:600px){.gallery-info-media-panel{max-height:clamp(240px,calc(var(--dialog-height,100dvh) - 440px),380px);overflow-y:auto;overscroll-behavior:contain}}
.gallery-info-media-tabs{display:flex;align-items:center;gap:24px;margin-bottom:16px;border-bottom:1px solid var(--ex-border)}
.gallery-info-media-tabs button{position:relative;min-height:40px;padding:8px 0 10px;border-radius:0;color:var(--ex-muted);font:18px/1.2 'EB Garamond',serif}
.gallery-info-media-tabs button[aria-selected=true]{color:var(--ex-ink)}
.gallery-info-media-tabs button::after{content:'';position:absolute;inset:auto 0 -1px;height:1px;background:currentColor;transform:scaleX(0);transition:transform 160ms ease}
.gallery-info-media-tabs button[aria-selected=true]::after{transform:scaleX(1)}
.gallery-info-media-tabs button:not(:disabled):active{transform:none}
@media(min-width:601px){.gallery-info-media :deep(.gallery-film){max-width:min(100%,max(300px,calc((var(--dialog-height,100dvh) - 390px)*16/9)));margin-inline:auto}}
.public-views-info-dialog.is-museum :deep(.plan-view){max-width:300px}
.public-views-info-dialog.is-museum .view-dialog-head{margin-bottom:16px}
.public-views-info-dialog.is-museum{--ex-paper:#f5f3ee;--ex-ink:#36332f;--ex-muted:#78716a;--ex-border:#d7d1c8;--ex-accent:#8c6d3e;color-scheme:light;}
.public-views-info-dialog.is-museum.is-dark{--ex-paper:#292623;--ex-ink:#e7e2da;--ex-muted:#bdb6ac;--ex-border:#5c554e;--ex-accent:#c5ad84;color-scheme:dark;box-shadow:0 18px 60px #0005;}
.public-views-info-dialog.is-museum :deep(.ai-view-term-mark){color:var(--ex-accent);}

.public-views-info-dialog.is-museum h2{font:34px/1.15 'EB Garamond',serif;font-weight:400;letter-spacing:-.02em}
.public-views-info-dialog.is-museum .view-dialog-close{border-radius:2px}
.public-views-tour{display:flex;align-items:center;justify-content:space-between;gap:20px;width:100%;margin:16px 0 0;padding:14px 0 0;border:0;border-top:1px solid var(--ex-border);border-radius:0;background:none;color:inherit;text-align:left;font:15px/1.35 'Quattrocento Sans',sans-serif;cursor:var(--site-interactive-cursor)}
.public-views-tour span{display:grid;gap:4px}.public-views-tour small{color:var(--ex-muted);font:10px/1.25 inherit;letter-spacing:.12em;text-transform:uppercase}.public-views-tour svg{width:19px;height:19px;fill:none;stroke:currentColor;stroke-width:1.2;transition:transform 220ms cubic-bezier(.22,1,.36,1)}
@media(max-width:600px){
  .public-views-info-dialog.is-museum{width:calc(var(--dialog-width,100vw) - 28px);max-height:calc(var(--dialog-height,100dvh) - 28px);padding:24px 20px 20px;border-radius:6px}
  .public-views-info-dialog.is-museum h2{font-size:29px}
  .public-views-info-dialog .view-dialog-head{margin-bottom:15px}
  .public-views-info-dialog.is-museum .public-views-info-copy{font-size:13px;line-height:1.55}
  .public-views-tour{margin-top:15px;padding-top:13px}
}
@media(min-width:601px) and (max-height:700px){
  .public-views-info-dialog.is-museum{width:min(900px,calc(var(--dialog-width,100vw) - 40px));padding:20px 28px 18px}
  .public-views-info-dialog.is-museum :deep(.museum-floor-plan){max-width:280px}
  .public-views-info-dialog.is-museum :deep(.plan-view){max-width:180px;margin-bottom:10px}
  .public-views-info-dialog.is-museum :deep(.museum-floor-guide){margin-top:10px;padding-top:10px}
  .public-views-info-dialog.is-museum :deep(.plan-location){margin-bottom:8px}
  .public-views-info-dialog.is-museum :deep(.plan-detail h3){margin-bottom:8px}
  .public-views-info-dialog.is-museum .view-dialog-head{margin-bottom:12px}
  .public-views-info-dialog.is-museum h2{font-size:29px}
  .gallery-introduction{font-size:13px;max-width:none}
  .gallery-introduction p+p{margin-top:8px}
  .public-views-tour{margin-top:10px;padding-top:10px}
}
@media(min-width:601px) and (max-height:500px){.public-views-info-dialog.is-museum{padding-block:16px}.public-views-tour{min-height:32px;font-size:13px;margin-top:8px;padding-top:8px}}
@media (hover: hover) and (pointer: fine) {
  .gallery-info-media-tabs button:hover:not(:disabled){background:none;color:var(--ex-ink)}
  .public-views-info-removal a:hover { color: var(--site-accent-ink); text-decoration-color: currentColor; }
  .public-views-tour:hover svg{transform:scale(1.08)}
}
@media (prefers-reduced-motion: reduce) {
  .gallery-info-media-tabs button::after{transition:none}
  .public-views-info-removal a,.public-views-tour svg { transition: none; }
  .public-views-info-dialog.is-museum{animation:none}
}
</style>

<style scoped>
.gallery-introduction strong{font-weight:700;color:#171614}
.is-dark .gallery-introduction strong{color:#f5f3ee}
</style>


<style scoped>
.public-views-info-dialog.is-museum .view-dialog-head{align-items:center;gap:22px}
@media(max-width:600px){.public-views-info-dialog.is-museum .view-dialog-head{gap:12px}.public-views-info-dialog.is-museum h2{font-size:24px;gap:8px}#public-views-info-title .gallery-mark{width:23px;height:24px}.public-views-design-entry :deep(.gallery-design-link){font-size:18px;gap:6px}.public-views-design-entry :deep(.design-entry-prefix){display:none}.public-views-design-entry :deep(.design-entry-word){text-transform:capitalize}.public-views-info-dialog .view-dialog-close{flex-basis:32px;width:32px;padding:7px;margin:0 -7px 0 0}}
@media(max-width:420px){.public-views-design-entry :deep(.design-entry-book){display:none}.public-views-design-entry :deep(.gallery-design-link){gap:0}}
@media(max-width:380px){.public-views-info-dialog.is-museum .view-dialog-head{gap:9px}.public-views-info-dialog.is-museum h2{font-size:22px;gap:6px}#public-views-info-title .gallery-mark{width:19px}}
</style>
